import { useRef, useState, useEffect } from 'react';
import Slider from 'react-slick';
import PropTypes from 'prop-types';

// Material-UI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { PaymentsOutlined, LocalShippingOutlined, HistoryOutlined } from '@mui/icons-material';

// components
import Image from 'components/Image/Image';

const CARDS = [
  {
    id: 1,
    textButton: 'Source Global',
    icon: <LocalShippingOutlined />,
    linkButton: `/delivery`,
  },
  {
    id: 2,
    textButton: 'Rated Excellent',
    icon: (
      <Image
        src="/assets/images/ctas/icon_trustpilot.png"
        width={51}
        height={24}
        alt="Trust Pilot icon"
        loading="eager"
      />
    ),
    linkButton: '',
  },
  {
    id: 3,
    textButton: 'Easy returns',
    icon: <HistoryOutlined />,
    linkButton: `/returns-policy`,
  },
  {
    id: 4,
    textButton: 'Price match promise',
    icon: <PaymentsOutlined />,
    linkButton: `https://wholesale.creoate.com/price-match-promise`,
  },
];

const MobileCarouselSkeleton = () => {
  return (
    <Stack direction="row" sx={{ justifyContent: { xs: 'center', sm: 'space-around' } }}>
      <Skeleton variant="rounded" animation="wave" width={150} />

      {/* Show two skeletons for tablets */}
      <Skeleton variant="rounded" animation="wave" width={150} sx={{ display: { xs: 'none', sm: 'inline-block' } }} />
    </Stack>
  );
};

const MobileCarousel = () => {
  const carouselRef = useRef(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => setHydrated(true), []);

  const settings = {
    arrows: false,
    infinite: true,
    slidesToShow: 2,
    autoplay: true,
    speed: 1500,
    autoplaySpeed: 6000,
    centerMode: false,
    centerPadding: '0px',
    responsive: [
      {
        breakpoint: 480,
        settings: { slidesToShow: 1 },
      },
    ],
  };

  return (
    <Box sx={{ position: 'relative', maxWidth: '700px', alignContent: 'center', height: 38 }}>
      {hydrated ? (
        <Slider ref={carouselRef} {...settings}>
          {CARDS.map((benefits) => (
            <Box key={benefits.id}>
              <BenefitsCard card={benefits} />
            </Box>
          ))}
        </Slider>
      ) : (
        <MobileCarouselSkeleton />
      )}
    </Box>
  );
};

const benefitsCardDefaultPropCard = {
  textButton: '',
  icon: null,
  linkButton: '',
};

const BenefitsCard = ({ card = benefitsCardDefaultPropCard }) => {
  const { textButton, icon, linkButton } = card;

  return (
    <Grid key={textButton} item xs={12} sm={12} md={4} lg={3}>
      <CardStyle>
        <Box>
          <BenefitsButton variant="text" startIcon={icon} href={linkButton} target="_blank">
            {textButton}
          </BenefitsButton>
        </Box>
      </CardStyle>
    </Grid>
  );
};

BenefitsCard.propTypes = {
  card: PropTypes.shape({
    textButton: PropTypes.string,
    icon: PropTypes.element,
    linkButton: PropTypes.string,
  }),
};

const CardStyle = styled(Card)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: 340,
    minHeight: 32,
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    boxShadow: 'none',
    backgroundColor: '#DFE3E8',
  };
});

const BenefitsButton = styled(Button)(() => {
  return {
    fontSize: '12px',
    zIndex: 0,
    textTransform: 'capitalize',
  };
});

const BenefitsTab = ({ isScrolled = false }) => {
  return (
    <Box
      sx={{
        backgroundColor: '#DFE3E8',
        display: 'block',
        overflow: 'hidden',
        height: {
          xs: isScrolled ? 0 : '38px',
          md: '38px',
        },
        opacity: {
          xs: isScrolled ? 0 : 1,
          md: 1,
        },
        transition: 'height 0.3s ease, opacity 0.3s ease',
      }}
      data-testid="benefits-tab-container"
      className={isScrolled ? 'scrolled' : 'visible'}
    >
      <Container maxWidth="100%" sx={{ display: { xs: 'block', md: 'none' } }}>
        <MobileCarousel />
      </Container>
      <Container maxWidth="xl" sx={{ display: { xs: 'none', md: 'block' } }}>
        <Grid
          container
          spacing={0}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'nowrap',
            height: '36px',
          }}
        >
          {CARDS.map((card) => (
            <BenefitsCard key={card.id} card={card} />
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default BenefitsTab;
